import { isCharacterId, type CharacterId } from './characters';

export type GameMode = 'highscore_race' | 'local_multiplayer';

/** Which cursor is live. `generic` means neither player is picking. */
export type ActiveCursor = 'player_1' | 'player_2' | 'generic';

export interface PlayerSelectState {
  mode: GameMode;
  cursor: ActiveCursor;
  selections: {
    1: CharacterId | null;
    2: CharacterId | null;
  };
  /** True once every player in the mode has locked in a racer. */
  readyToRace: boolean;
}

export type SelectionOutcome =
  | { ok: true; state: PlayerSelectState }
  | { ok: false; reason: SelectionRejection; state: PlayerSelectState };

export type SelectionRejection =
  | 'unknown_character'
  | 'already_taken'
  | 'no_active_player';

export function createPlayerSelectState(mode: GameMode): PlayerSelectState {
  return {
    mode,
    cursor: 'player_1',
    selections: { 1: null, 2: null },
    readyToRace: false,
  };
}

/**
 * Pure reducer for the player select screen.
 *
 * Returns a new state rather than mutating, so scenes stay thin and the
 * selection rules can be tested without booting Phaser.
 */
export function selectCharacter(
  state: PlayerSelectState,
  character: string,
): SelectionOutcome {
  if (!isCharacterId(character)) {
    return { ok: false, reason: 'unknown_character', state };
  }

  if (state.cursor === 'generic') {
    return { ok: false, reason: 'no_active_player', state };
  }

  const player = state.cursor === 'player_1' ? 1 : 2;
  const other = player === 1 ? 2 : 1;

  // Two racers cannot drive the same character in local multiplayer.
  if (state.selections[other] === character) {
    return { ok: false, reason: 'already_taken', state };
  }

  const selections = { ...state.selections, [player]: character };

  // Player 1 hands the cursor to player 2 only in local multiplayer.
  const handOffToPlayerTwo = player === 1 && state.mode === 'local_multiplayer';
  const cursor: ActiveCursor = handOffToPlayerTwo ? 'player_2' : 'generic';

  return {
    ok: true,
    state: {
      ...state,
      cursor,
      selections,
      readyToRace: isReadyToRace(state.mode, selections),
    },
  };
}

export function isReadyToRace(
  mode: GameMode,
  selections: PlayerSelectState['selections'],
): boolean {
  if (mode === 'highscore_race') {
    return selections[1] !== null;
  }
  return selections[1] !== null && selections[2] !== null;
}

/** Racers taken by someone other than the player currently choosing. */
export function unavailableCharacters(
  state: PlayerSelectState,
): readonly CharacterId[] {
  if (state.cursor === 'player_2' && state.selections[1]) {
    return [state.selections[1]];
  }
  if (state.cursor === 'player_1' && state.selections[2]) {
    return [state.selections[2]];
  }
  return [];
}
